import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatPoints } from '../types/loyalty';
import { Award, Loader2 } from 'lucide-react';

interface LoyaltyPointsDisplayProps {
  points: number | null;
  tierName: string | null;
  tierColor: string | null;
  isLoading?: boolean;
  className?: string;
  compact?: boolean;
}

const LoyaltyPointsDisplay: React.FC<LoyaltyPointsDisplayProps> = ({
  points,
  tierName,
  tierColor,
  isLoading = false,
  className = '',
  compact = false
}) => {
  const { t } = useTranslation();
  // Default styling if no tier color provided
  const defaultColor = '#6B7280'; // Gray
  const displayColor = tierColor || defaultColor;

  if (isLoading) {
    return (
      <div className={`flex items-center ${className}`}>
        <Loader2 className="w-4 h-4 mr-1 animate-spin text-gray-400" />
        <span className="text-sm text-gray-400">{t('common.loading')}</span>
      </div>
    );
  }
  
  // Return empty component if no points (instead of null to avoid unmounting)
  if (points === null || points === undefined) {
    if (compact) {
      return (
        <div className={`flex items-center ${className}`}>
          <Award 
            className="w-4 h-4 mr-1 text-gray-400" 
          />
          <span className="text-sm font-medium text-gray-400">
            0
          </span>
        </div>
      );
    }
    return (
      <div className={`flex items-center ${className}`}>
        <Award 
          className="w-5 h-5 mr-2 text-gray-400" 
        />
        <div className="flex flex-col">
          {tierName && (
            <span className="text-xs font-semibold text-gray-400">
              {tierName}
            </span>
          )}
          <span className="text-sm font-medium text-gray-400">
            {t('loyalty.pointsWithCount', { count: 0 })}
          </span>
        </div>
      </div>
    );
  }
  
  // Compact version for navigation bar
  if (compact) {
    return (
      <div className={`flex items-center ${className}`}>
        <div className="flex items-center space-x-2">
          <Award 
            className="w-4 h-4"
            style={{ color: displayColor }} 
          />
          <span className="text-white">
            {formatPoints(points)}
          </span>
        </div>
      </div>
    );
  }
  
  // Full version with tier name
  return (
    <div className={`flex items-center ${className}`}>
      <div className="flex items-center space-x-2">
        <Award 
          className="w-5 h-5"
          style={{ color: displayColor }} 
        />
        {!compact && (
          <span className="text-white ml-2">
            {t('loyalty.pointsWithFormatted', { points: formatPoints(points) })}
          </span>
        )}
      </div>
      {compact ? (
        <span className="text-white">
          {formatPoints(points)}
        </span>
      ) : (
        <span className="text-sm text-white">
          {tierName}
        </span>
      )}
    </div>
  );
};

export default LoyaltyPointsDisplay;